import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, set } from "firebase/database";
import { FaSave, FaDownload, FaBell, FaShieldAlt, FaCreditCard, FaTruck, FaGlobe, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import AdminLayout from "../components/AdminLayout";
import "./AdminSettings.css";
import { setPageMeta } from "../utils/seo";

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setPageMeta({
      title: "Admin Settings | Banat India",
      description:
        "Configure site, payment, and notification settings in the Banat India admin panel.",
      keywords: "admin settings, Banat India admin, site configuration",
    });
  }, []);

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    siteName: "Banat",
    siteDescription: "Premium toothbrushes for all ages",
    contactEmail: "admin@banat.com",
    contactPhone: "+91 98765 43210",
    maintenanceMode: false,
    currency: "INR",
    timezone: "Asia/Kolkata"
  });

  // Payment Settings
  const [paymentSettings, setPaymentSettings] = useState({
    razorpayKeyId: "rzp_test_xxxxxxxxxxxxx",
    razorpayKeySecret: "xxxxxxxxxxxxxxxxxxxxxxxx",
    testMode: true,
    autoCapture: true,
    paymentMethods: ["card", "netbanking", "upi", "wallet"]
  });

  // Shipping Settings
  const [shippingSettings, setShippingSettings] = useState({
    shiprocketEmail: "admin@banat.com",
    shiprocketPassword: "********",
    defaultCourier: "DTDC",
    freeShippingThreshold: 500,
    shippingZones: ["Domestic", "International"]
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    orderNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
    adminEmail: "admin@banat.com",
    notificationEmail: "notifications@banat.com"
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: 30,
    requireTwoFactor: false,
    maxLoginAttempts: 5,
    passwordExpiry: 90,
    ipWhitelist: []
  });

  // Load settings from Firebase
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const database = getDatabase();
        const settingsRef = ref(database, "adminSettings");
        
        const snapshot = await get(settingsRef);
        
        if (snapshot.exists()) {
          const settings = snapshot.val();
          if (settings.general) setGeneralSettings(settings.general);
          if (settings.payment) setPaymentSettings(settings.payment);
          if (settings.shipping) setShippingSettings(settings.shipping);
          if (settings.notifications) setNotificationSettings(settings.notifications);
          if (settings.security) setSecuritySettings(settings.security);
        } else {
          // Create default settings if none exist
          await createDefaultSettings();
        }
        
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    const createDefaultSettings = async () => {
      const database = getDatabase();
      const settingsRef = ref(database, "adminSettings");
      
      const defaultSettings = {
        general: generalSettings,
        payment: paymentSettings,
        shipping: shippingSettings,
        notifications: notificationSettings,
        security: securitySettings,
        lastUpdated: new Date().toISOString()
      };
      
      await set(settingsRef, defaultSettings);
    };

    loadSettings();
  }, [generalSettings, notificationSettings, paymentSettings, securitySettings, shippingSettings]);

  // Validate settings before saving
  const validateSettings = (settingsType) => {
    const errors = {};

    if (settingsType === "general") {
      if (!generalSettings.siteName.trim()) {
        errors.siteName = "Site name is required";
      }
      if (!generalSettings.contactEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(generalSettings.contactEmail)) {
        errors.contactEmail = "Valid email is required";
      }
      if (!generalSettings.contactPhone.trim()) {
        errors.contactPhone = "Contact phone is required";
      }
    }

    if (settingsType === "payment") {
      if (!paymentSettings.razorpayKeyId.trim()) {
        errors.razorpayKeyId = "Razorpay Key ID is required";
      }
      if (!paymentSettings.razorpayKeySecret.trim()) {
        errors.razorpayKeySecret = "Razorpay Key Secret is required";
      }
      if (paymentSettings.paymentMethods.length === 0) {
        errors.paymentMethods = "At least one payment method must be selected";
      }
    }

    if (settingsType === "shipping") {
      if (!shippingSettings.shiprocketEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingSettings.shiprocketEmail)) {
        errors.shiprocketEmail = "Valid Shiprocket email is required";
      }
      if (shippingSettings.freeShippingThreshold < 0) {
        errors.freeShippingThreshold = "Free shipping threshold must be positive";
      }
    }

    if (settingsType === "notifications") {
      if (!notificationSettings.adminEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notificationSettings.adminEmail)) {
        errors.adminEmail = "Valid admin email is required";
      }
    }

    if (settingsType === "security") {
      if (securitySettings.sessionTimeout < 5 || securitySettings.sessionTimeout > 480) {
        errors.sessionTimeout = "Session timeout must be between 5 and 480 minutes";
      }
      if (securitySettings.maxLoginAttempts < 3 || securitySettings.maxLoginAttempts > 10) {
        errors.maxLoginAttempts = "Max login attempts must be between 3 and 10";
      }
      if (securitySettings.passwordExpiry < 30 || securitySettings.passwordExpiry > 365) {
        errors.passwordExpiry = "Password expiry must be between 30 and 365 days";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (settingsType) => {
    if (!validateSettings(settingsType)) {
      setSaveStatus("Please fix validation errors before saving");
      setTimeout(() => setSaveStatus(""), 3000);
      return;
    }

    setSaving(true);
    setSaveStatus("Saving settings...");
    
    try {
      const database = getDatabase();
      const settingsRef = ref(database, "adminSettings");
      
      // Get current settings
      const snapshot = await get(settingsRef);
      const currentSettings = snapshot.exists() ? snapshot.val() : {};
      
      // Update specific settings
      let updatedSettings;
      switch (settingsType) {
        case "general":
          updatedSettings = { ...currentSettings, general: generalSettings };
          break;
        case "payment":
          updatedSettings = { ...currentSettings, payment: paymentSettings };
          break;
        case "shipping":
          updatedSettings = { ...currentSettings, shipping: shippingSettings };
          break;
        case "notifications":
          updatedSettings = { ...currentSettings, notifications: notificationSettings };
          break;
        case "security":
          updatedSettings = { ...currentSettings, security: securitySettings };
          break;
        default:
          updatedSettings = {
            general: generalSettings,
            payment: paymentSettings,
            shipping: shippingSettings,
            notifications: notificationSettings,
            security: securitySettings
          };
      }
      
      updatedSettings.lastUpdated = new Date().toISOString();
      
      await set(settingsRef, updatedSettings);
      
      setSaveStatus("Settings saved successfully!");
      setTimeout(() => setSaveStatus(""), 3000);
      
      // Apply settings in real-time
      applySettings(settingsType);
      
    } catch (error) {
      setSaveStatus("Error saving settings");
      setTimeout(() => setSaveStatus(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Apply settings in real-time
  const applySettings = (settingsType) => {
    switch (settingsType) {
      case "general":
        // Apply site name and description
        document.title = generalSettings.siteName;
        // You could also update meta tags, site branding, etc.
        break;
      case "payment":
        break;
      case "shipping":
        break;
      case "notifications":
        break;
      case "security":
        break;
      default:
        break;
    }
  };

  const exportSettings = () => {
    const allSettings = {
      general: generalSettings,
      payment: paymentSettings,
      shipping: shippingSettings,
      notifications: notificationSettings,
      security: securitySettings,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(allSettings, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderGeneralSettings = () => (
    <div className="settings-section">
      <h3>General Settings</h3>
      <div className="settings-grid">
        <div className="setting-item">
          <label>Site Name *</label>
          <input
            type="text"
            value={generalSettings.siteName}
            onChange={(e) => setGeneralSettings({...generalSettings, siteName: e.target.value})}
            className={validationErrors.siteName ? 'error' : ''}
          />
          {validationErrors.siteName && (
            <span className="error-message">{validationErrors.siteName}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Site Description</label>
          <textarea
            value={generalSettings.siteDescription}
            onChange={(e) => setGeneralSettings({...generalSettings, siteDescription: e.target.value})}
            rows="3"
          />
        </div>
        <div className="setting-item">
          <label>Contact Email *</label>
          <input
            type="email"
            value={generalSettings.contactEmail}
            onChange={(e) => setGeneralSettings({...generalSettings, contactEmail: e.target.value})}
            className={validationErrors.contactEmail ? 'error' : ''}
          />
          {validationErrors.contactEmail && (
            <span className="error-message">{validationErrors.contactEmail}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Contact Phone *</label>
          <input
            type="tel"
            value={generalSettings.contactPhone}
            onChange={(e) => setGeneralSettings({...generalSettings, contactPhone: e.target.value})}
            className={validationErrors.contactPhone ? 'error' : ''}
          />
          {validationErrors.contactPhone && (
            <span className="error-message">{validationErrors.contactPhone}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Currency</label>
          <select
            value={generalSettings.currency}
            onChange={(e) => setGeneralSettings({...generalSettings, currency: e.target.value})}
          >
            <option value="INR">Indian Rupee (₹)</option>
            <option value="USD">US Dollar ($)</option>
            <option value="EUR">Euro (€)</option>
          </select>
        </div>
        <div className="setting-item">
          <label>Timezone</label>
          <select
            value={generalSettings.timezone}
            onChange={(e) => setGeneralSettings({...generalSettings, timezone: e.target.value})}
          >
            <option value="Asia/Kolkata">India (IST)</option>
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time</option>
          </select>
        </div>
        <div className="setting-item checkbox">
          <label>
            <input
              type="checkbox"
              checked={generalSettings.maintenanceMode}
              onChange={(e) => setGeneralSettings({...generalSettings, maintenanceMode: e.target.checked})}
            />
            Maintenance Mode
          </label>
        </div>
      </div>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="settings-section">
      <h3>Payment Settings</h3>
      <div className="settings-grid">
        <div className="setting-item">
          <label>Razorpay Key ID *</label>
          <input
            type="text"
            value={paymentSettings.razorpayKeyId}
            onChange={(e) => setPaymentSettings({...paymentSettings, razorpayKeyId: e.target.value})}
            placeholder="rzp_test_xxxxxxxxxxxxx"
            className={validationErrors.razorpayKeyId ? 'error' : ''}
          />
          {validationErrors.razorpayKeyId && (
            <span className="error-message">{validationErrors.razorpayKeyId}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Razorpay Key Secret *</label>
          <input
            type="password"
            value={paymentSettings.razorpayKeySecret}
            onChange={(e) => setPaymentSettings({...paymentSettings, razorpayKeySecret: e.target.value})}
            placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
            className={validationErrors.razorpayKeySecret ? 'error' : ''}
          />
          {validationErrors.razorpayKeySecret && (
            <span className="error-message">{validationErrors.razorpayKeySecret}</span>
          )}
        </div>
        <div className="setting-item checkbox">
          <label>
            <input
              type="checkbox"
              checked={paymentSettings.testMode}
              onChange={(e) => setPaymentSettings({...paymentSettings, testMode: e.target.checked})}
            />
            Test Mode
          </label>
        </div>
        <div className="setting-item checkbox">
          <label>
            <input
              type="checkbox"
              checked={paymentSettings.autoCapture}
              onChange={(e) => setPaymentSettings({...paymentSettings, autoCapture: e.target.checked})}
            />
            Auto Capture Payments
          </label>
        </div>
        <div className="setting-item">
          <label>Payment Methods *</label>
          <div className="checkbox-group">
            {["card", "netbanking", "upi", "wallet"].map(method => (
              <label key={method} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={paymentSettings.paymentMethods.includes(method)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPaymentSettings({
                        ...paymentSettings,
                        paymentMethods: [...paymentSettings.paymentMethods, method]
                      });
                    } else {
                      setPaymentSettings({
                        ...paymentSettings,
                        paymentMethods: paymentSettings.paymentMethods.filter(m => m !== method)
                      });
                    }
                  }}
                />
                {method.charAt(0).toUpperCase() + method.slice(1)}
              </label>
            ))}
          </div>
          {validationErrors.paymentMethods && (
            <span className="error-message">{validationErrors.paymentMethods}</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderShippingSettings = () => (
    <div className="settings-section">
      <h3>Shipping Settings</h3>
      <div className="settings-grid">
        <div className="setting-item">
          <label>Shiprocket Email *</label>
          <input
            type="email"
            value={shippingSettings.shiprocketEmail}
            onChange={(e) => setShippingSettings({...shippingSettings, shiprocketEmail: e.target.value})}
            className={validationErrors.shiprocketEmail ? 'error' : ''}
          />
          {validationErrors.shiprocketEmail && (
            <span className="error-message">{validationErrors.shiprocketEmail}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Shiprocket Password</label>
          <input
            type="password"
            value={shippingSettings.shiprocketPassword}
            onChange={(e) => setShippingSettings({...shippingSettings, shiprocketPassword: e.target.value})}
          />
        </div>
        <div className="setting-item">
          <label>Default Courier</label>
          <select
            value={shippingSettings.defaultCourier}
            onChange={(e) => setShippingSettings({...shippingSettings, defaultCourier: e.target.value})}
          >
            <option value="DTDC">DTDC</option>
            <option value="BlueDart">BlueDart</option>
            <option value="FedEx">FedEx</option>
            <option value="DHL">DHL</option>
          </select>
        </div>
        <div className="setting-item">
          <label>Free Shipping Threshold (₹)</label>
          <input
            type="number"
            value={shippingSettings.freeShippingThreshold}
            onChange={(e) => setShippingSettings({...shippingSettings, freeShippingThreshold: parseInt(e.target.value)})}
            min="0"
            className={validationErrors.freeShippingThreshold ? 'error' : ''}
          />
          {validationErrors.freeShippingThreshold && (
            <span className="error-message">{validationErrors.freeShippingThreshold}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Shipping Zones</label>
          <div className="checkbox-group">
            {["Domestic", "International"].map(zone => (
              <label key={zone} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={shippingSettings.shippingZones.includes(zone)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setShippingSettings({
                        ...shippingSettings,
                        shippingZones: [...shippingSettings.shippingZones, zone]
                      });
                    } else {
                      setShippingSettings({
                        ...shippingSettings,
                        shippingZones: shippingSettings.shippingZones.filter(z => z !== zone)
                      });
                    }
                  }}
                />
                {zone}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3>Notification Settings</h3>
      <div className="settings-grid">
        <div className="setting-item">
          <label>Admin Email *</label>
          <input
            type="email"
            value={notificationSettings.adminEmail}
            onChange={(e) => setNotificationSettings({...notificationSettings, adminEmail: e.target.value})}
            className={validationErrors.adminEmail ? 'error' : ''}
          />
          {validationErrors.adminEmail && (
            <span className="error-message">{validationErrors.adminEmail}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Notification Email</label>
          <input
            type="email"
            value={notificationSettings.notificationEmail}
            onChange={(e) => setNotificationSettings({...notificationSettings, notificationEmail: e.target.value})}
          />
        </div>
        <div className="setting-item checkbox">
          <label>
            <input
              type="checkbox"
              checked={notificationSettings.orderNotifications}
              onChange={(e) => setNotificationSettings({...notificationSettings, orderNotifications: e.target.checked})}
            />
            Order Notifications
          </label>
        </div>
        <div className="setting-item checkbox">
          <label>
            <input
              type="checkbox"
              checked={notificationSettings.emailNotifications}
              onChange={(e) => setNotificationSettings({...notificationSettings, emailNotifications: e.target.checked})}
            />
            Email Notifications
          </label>
        </div>
        <div className="setting-item checkbox">
          <label>
            <input
              type="checkbox"
              checked={notificationSettings.smsNotifications}
              onChange={(e) => setNotificationSettings({...notificationSettings, smsNotifications: e.target.checked})}
            />
            SMS Notifications
          </label>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3>Security Settings</h3>
      <div className="settings-grid">
        <div className="setting-item">
          <label>Session Timeout (minutes)</label>
          <input
            type="number"
            value={securitySettings.sessionTimeout}
            onChange={(e) => setSecuritySettings({...securitySettings, sessionTimeout: parseInt(e.target.value)})}
            min="5"
            max="480"
            className={validationErrors.sessionTimeout ? 'error' : ''}
          />
          {validationErrors.sessionTimeout && (
            <span className="error-message">{validationErrors.sessionTimeout}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Max Login Attempts</label>
          <input
            type="number"
            value={securitySettings.maxLoginAttempts}
            onChange={(e) => setSecuritySettings({...securitySettings, maxLoginAttempts: parseInt(e.target.value)})}
            min="3"
            max="10"
            className={validationErrors.maxLoginAttempts ? 'error' : ''}
          />
          {validationErrors.maxLoginAttempts && (
            <span className="error-message">{validationErrors.maxLoginAttempts}</span>
          )}
        </div>
        <div className="setting-item">
          <label>Password Expiry (days)</label>
          <input
            type="number"
            value={securitySettings.passwordExpiry}
            onChange={(e) => setSecuritySettings({...securitySettings, passwordExpiry: parseInt(e.target.value)})}
            min="30"
            max="365"
            className={validationErrors.passwordExpiry ? 'error' : ''}
          />
          {validationErrors.passwordExpiry && (
            <span className="error-message">{validationErrors.passwordExpiry}</span>
          )}
        </div>
        <div className="setting-item checkbox">
          <label>
            <input
              type="checkbox"
              checked={securitySettings.requireTwoFactor}
              onChange={(e) => setSecuritySettings({...securitySettings, requireTwoFactor: e.target.checked})}
            />
            Require Two-Factor Authentication
          </label>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading settings...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-content-header">
        <h1>Admin Settings</h1>
        <div className="admin-actions">
          <button className="export-btn" onClick={exportSettings}>
            <FaDownload /> Export Settings
          </button>
          {saveStatus && (
            <span className={`save-status ${saveStatus.includes('success') ? 'success' : saveStatus.includes('error') ? 'error' : ''}`}>
              {saveStatus.includes('success') ? <FaCheckCircle /> : saveStatus.includes('error') ? <FaExclamationTriangle /> : null}
              {saveStatus}
            </span>
          )}
        </div>
      </div>

      <div className="settings-tabs">
        <button 
          className={`tab-btn ${activeTab === "general" ? "active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          <FaGlobe /> General
        </button>
        <button 
          className={`tab-btn ${activeTab === "payment" ? "active" : ""}`}
          onClick={() => setActiveTab("payment")}
        >
          <FaCreditCard /> Payment
        </button>
        <button 
          className={`tab-btn ${activeTab === "shipping" ? "active" : ""}`}
          onClick={() => setActiveTab("shipping")}
        >
          <FaTruck /> Shipping
        </button>
        <button 
          className={`tab-btn ${activeTab === "notifications" ? "active" : ""}`}
          onClick={() => setActiveTab("notifications")}
        >
          <FaBell /> Notifications
        </button>
        <button 
          className={`tab-btn ${activeTab === "security" ? "active" : ""}`}
          onClick={() => setActiveTab("security")}
        >
          <FaShieldAlt /> Security
        </button>
      </div>

      <div className="settings-content">
        {activeTab === "general" && renderGeneralSettings()}
        {activeTab === "payment" && renderPaymentSettings()}
        {activeTab === "shipping" && renderShippingSettings()}
        {activeTab === "notifications" && renderNotificationSettings()}
        {activeTab === "security" && renderSecuritySettings()}

        <div className="settings-actions">
          <button 
            className="save-btn" 
            onClick={() => handleSave(activeTab)}
            disabled={saving}
          >
            <FaSave />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings; 